/**
 * Shopping Lists slice — the household's lists + the currently active list
 * with its items.
 *
 * Ported from legacy `useStore.ts`:
 *   ShoppingLists, activeShoppingList, activeListItems,
 *   setShoppingLists, setActiveShoppingList,
 *   addShoppingListItem, updateShoppingListItem, removeShoppingListItem.
 *
 * Purchase flow note: legacy `markItemPurchased` calls the backend and then
 * updates state. That is an orchestrator-side flow; the pure reducer here
 * only accepts the merged result (via `updateItem` with
 * `{ status: 'purchased', checked: true, purchased_at, purchased_by }`).
 *
 * Pure: no Date.now, no fetch, no storage (CP3). Reducers for no-ops return
 * the same state reference to help subscribers skip renders.
 */

import type {
  ShoppingList,
  ShoppingListItemWithDetails,
} from '../types/shopping-list';

export interface ShoppingListsState {
  /** All lists known to the client (household-scoped). */
  lists: ShoppingList[];
  /** The list the UI is currently showing items for. */
  activeList: ShoppingList | null;
  /** Items belonging to `activeList`. Empty if no active list. */
  activeItems: ShoppingListItemWithDetails[];
}

export const initialShoppingListsState: ShoppingListsState = {
  lists: [],
  activeList: null,
  activeItems: [],
};

// ---------------------------------------------------------------------------
// Actions — collections
// ---------------------------------------------------------------------------

export function setLists(
  state: ShoppingListsState,
  input: { lists: ShoppingList[] }
): ShoppingListsState {
  return { ...state, lists: input.lists };
}

export function setActiveList(
  state: ShoppingListsState,
  input: { list: ShoppingList | null; items: ShoppingListItemWithDetails[] }
): ShoppingListsState {
  return { ...state, activeList: input.list, activeItems: input.items };
}

// ---------------------------------------------------------------------------
// Actions — item CRUD on the active list
// ---------------------------------------------------------------------------

export function addItem(
  state: ShoppingListsState,
  input: { item: ShoppingListItemWithDetails }
): ShoppingListsState {
  return {
    ...state,
    activeItems: [...state.activeItems, input.item],
  };
}

export function updateItem(
  state: ShoppingListsState,
  input: { itemId: string; changes: Partial<ShoppingListItemWithDetails> }
): ShoppingListsState {
  let changed = false;
  const next = state.activeItems.map((it) => {
    if (it.id !== input.itemId) return it;
    changed = true;
    return { ...it, ...input.changes };
  });
  if (!changed) return state;
  return { ...state, activeItems: next };
}

export function removeItem(
  state: ShoppingListsState,
  input: { itemId: string }
): ShoppingListsState {
  const next = state.activeItems.filter((it) => it.id !== input.itemId);
  if (next.length === state.activeItems.length) return state;
  return { ...state, activeItems: next };
}

// ---------------------------------------------------------------------------
// Derived helpers (pure selectors) — useful to reducers and tests; UI-facing
// display helpers like getItemDisplayTitle / groupItemsByStore stay in the
// app layer (legacy had them in the service file).
// ---------------------------------------------------------------------------

export function findItem(
  state: ShoppingListsState,
  itemId: string
): ShoppingListItemWithDetails | undefined {
  return state.activeItems.find((it) => it.id === itemId);
}

export function findList(
  state: ShoppingListsState,
  listId: string
): ShoppingList | undefined {
  return state.lists.find((l) => l.id === listId);
}
